import { useCallback, useEffect, useState } from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

const GET_SECURITIES = gql`
    query SearchSecurities($ids: [Int!], $take: Int = 100, $skip: Int = 0) {
        securities(take: $take, skip: $skip, where: { id: { in: $ids } }) {
            items {
                id
                name
                currency {
                    id
                    threeLetterIsoCode
                }
                isin
                factsheet
            }
        }
    }
`;

interface Security {
    id: number;
    name: string;
    currency: {
        id: number;
        threeLetterIsoCode: string;
    };
}

interface UseGetSecuritiesState {
    data: Security[];
    loading: boolean;
    error: unknown;
}

const useGetSecurities = ({
    ids,
    take = 100,
    skip = 0,
    loadInitially = true,
    query = GET_SECURITIES,
    options: { staleTime, gcTime } = {},
} : {
        ids?: number[],
        take?: number,
        skip?: number,
        loadInitially?: boolean,
        query?: any,
        options?: { staleTime?: number, gcTime?: number }
    }) => {
    const client = useApolloClient();
    const queryClient = useQueryClient();
    const { i18n: { language } } = useTranslation();

    const [state, setState] = useState<UseGetSecuritiesState>({
        data: [],
        loading: false,
        error: null,
    });

    const fetchSecurities = useCallback(async ({ securityIds = ids } : { securityIds?: number[] } = {}): Promise<Security[]> => {
        if (!securityIds?.length) {
            setState({ data: [], loading: false, error: null });

            return [];
        }

        setState((prev) => ({ ...prev, loading: true, error: null }));

        try {
            const securities: Security[] = await queryClient.fetchQuery({
                queryKey: ['securities', securityIds, language],
                queryFn: async () => {
                    const response = await client.query({
                        query,
                        variables: { ids: securityIds, take, skip },
                        fetchPolicy: 'cache-first',
                    });

                    return response?.data?.securities?.items ?? [];
                },
                staleTime: staleTime ?? 10 * 60 * 1000,
                gcTime: gcTime ?? 20 * 60 * 1000,
            });

            setState({ data: securities, loading: false, error: null });

            return securities;
        } catch (error) {
            setState((prev) => ({ ...prev, loading: false, error }));

            return [];
        }
    }, [client, queryClient, ids, language, query, staleTime, gcTime]);

    useEffect(() => {
        if (loadInitially && ids?.length) {
            fetchSecurities({ securityIds: ids });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadInitially, JSON.stringify(ids), language]);

    return {
        data: state.data,
        loading: state.loading,
        error: state.error,
        refetch: fetchSecurities,
    };
};

export default useGetSecurities;
